import {
  AudioPreviewOptionsType,
  BlurLevel,
  ClusterSimThresholdType,
  GlobalSettings,
  OverlayPreviewOptionsType,
  RedactionConfig,
  TextOverlay,
  TextOverlaySize,
  TranscriptionViewable,
} from '@common/state/modules/mediaDetails/models';
import { AUDIO_TONE_LEVEL } from '@redact/components/MediaDetailsPage/MediaDetailsTopBar/GlobalSettingsModal/ToneVolumeSelector';
import { SettingsProfileType } from '@redact/state/modules/settingsProfile/models';
import { ElementOf, ValueOf } from 'ts-essentials';
import { v4 as uuid } from 'uuid';

export const DEFAULT_THUMBNAIL_REQUEST_TIMEOUT = 10_000;

export const DOCUMENT_REDACTION_MIME_TYPES = [
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document', // DOCX
  'application/vnd.openxmlformats-officedocument.presentationml.presentation', // PPTX
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // XLSX
  'application/pdf', // PDF
  'application/rtf', // RTF
  'text/rtf', // RTF alternate
  'text/plain', // TXT
  'text/plain; charset=utf-8', // TXT alternate
  'message/rfc822', // EML
  'application/vnd.ms-outlook', // MSG
] as const;

export const DOCUMENT_REDACTION_EXTENSIONS = [
  '.pdf',
  '.docx',
  '.eml',
  '.msg',
  '.pptx',
  '.xlsx',
  '.txt',
  '.rtf',
] as const;

export const DEFAULT_THUMBNAIL_RUNNING_QUEUE_TIMEOUT = 20_000;
export const DEFAULT_TRASH_RETENTION_DAYS = 0;

export type DETECTION_OBJECT_TYPE =
  | 'head'
  | 'poim'
  | 'licensePlate'
  | 'laptop'
  | 'vehicle'
  | 'notepad'
  | 'card'
  | 'person';

export type OBJECT_TYPE = DETECTION_OBJECT_TYPE | 'udr';
export type GROUPED_POLY_TYPE = OBJECT_TYPE | 'text_overlay';
export type SEGMENT_TYPE = OBJECT_TYPE; // technically don't need a different type
export type GROUP_TYPE = OBJECT_TYPE; // technically don't need a different type
export type CLUSTER_TYPE = GROUP_TYPE | 'mixed';

export const SEGMENT_SORT_BY_TYPE_ORDER = [
  'udr' as const,
  'poim' as const,
  'head' as const,
  'person' as const,
  'card' as const,
  'laptop' as const,
  'notepad' as const,
  'licensePlate' as const,
  'vehicle' as const,
];

export const MIXED_TYPE_ORDER = [
  'poim' as const,
  'head' as const,
  'person' as const,
  'card' as const,
  'laptop' as const,
  'notepad' as const,
  'vehicle' as const,
  'licensePlate' as const,
  'udr' as const,
];

export const DETECTION_TYPES = [
  'head' as const,
  'licensePlate' as const,
  'laptop' as const,
  'vehicle' as const,
  'notepad' as const,
  'card' as const,
  'poim' as const,
  'person' as const,
];

// preference for sorting/viewing cluster segments
export type CLUSTER_SEGMENT_SORT_TYPE =
  | 'time' // sort segments by startTime - DEFAULT
  | 'type' // sort by object type (then startTime)
  | 'group'; // sort by original group (then startTime)

export type SCALABLE_TYPES =
  | Exclude<OBJECT_TYPE, 'licensePlate' | 'poim' | 'udr'>
  | 'plate';
export const GAPABLE_TYPES = ['head', 'licensePlate', 'poim'];
export const MANUAL_TRACKING_CHANGE_INTERVAL_MS = 33; // manual tracking a.k.a spray painting

export const OBJECT_EFFECT_TYPES: (SCALABLE_TYPES | 'udr')[] = [
  'udr',
  'head',
  'person',
  'laptop',
  'card',
  'notepad',
  'vehicle',
  'plate',
];

export const DETECTION_FILTER_PARAMETER_TYPES = [
  // 'all',
  'head',
  'person',
  'laptop',
  'card',
  'notepad',
  'licensePlate',
  'poim',
] as const;

export const FILTER_PARAMETER_TYPES = [
  // 'all',
  'udr',
  'head',
  'person',
  'laptop',
  'card',
  'notepad',
  'licensePlate',
  'poim',
] as const;

export const DETECTION_COLLECTION_KEYS = FILTER_PARAMETER_TYPES; // same

export const TEXT_OVERLAY_FILTER_PARAMETER_TYPE = 'textOverlay' as const;

export type FILTER_PARAMETER_TYPE = ValueOf<typeof FILTER_PARAMETER_TYPES>;
export type VIEW_FILTER_PARAMETER_TYPE =
  | FILTER_PARAMETER_TYPE
  | typeof TEXT_OVERLAY_FILTER_PARAMETER_TYPE;
export type DETECTION_FILTER_PARAMETER_TYPE = ValueOf<
  typeof DETECTION_FILTER_PARAMETER_TYPES
>;

export const convertPolyToScalable = (
  type?: GROUPED_POLY_TYPE
): SCALABLE_TYPES | undefined => {
  if (!type || type === 'udr' || type === 'text_overlay' || type === 'poim') {
    return;
  } else if (type === 'licensePlate') {
    return 'plate';
  }
  return type;
};

export const UDR_COLOR = '#FFEB3B';
// Trial flag: use a light-neutral accent for text overlays (distinct from the
// detection hue-coding and the reddish Person/red-text colours) instead of the
// original terracotta. Drives the timeline track AND the on-canvas box accent /
// cluster indicator (the feature's shared accent colour). Flip to false to
// revert to '#D95D46'.
const USE_NEUTRAL_TEXT_OVERLAY_COLOR = false;
export const TEXT_OVERLAY_TRACK_COLOR = USE_NEUTRAL_TEXT_OVERLAY_COLOR
  ? '#E8EAED'
  : '#D95D46';
export const TEXT_OVERLAY_TRACK_STROKE = '#B84A36';
export const HEAD_COLOR = '#00A077';
export const PLATE_COLOR = '#00B7EC';
export const VEHICLE_COLOR = '#00B7EC'; // continue to use plate color for now
// export const VEHICLE_COLOR = '#146BEE'; // this is different shade of blue that we may use in the future
export const LAPTOP_COLOR = '#F3640D';
export const POIM_COLOR = '#C933F2';
export const OUTLINE_COLOR = '#FFF';
export const NOTEPAD_COLOR = '#BF40BF';
export const CARD_COLOR = '#1274EC';
export const PERSON_COLOR = '#ed405d';
export const MIXED_COLOR = '#9E9E9E';
export const MERGED_COLOR = '#8257EF';

export const UNSELECTED_ICON_COLOR = 'rgba(200, 200, 200, 0.5)';
export const TIMELINE_ICON_COLOR = '#B3BEC4';

export const PLAYBACK_RATES = [0.25, 0.5, 1, 1.25, 1.5, 1.75, 2, 4, 8] as const;

export const TRANS_HEIGHT_PERCENT = 0.7;

export const TDO_GRID_COLUMN_WIDTH = 315;

export const BOUNDING_POLY_PRECISION = 6;
export const TIME_MS_PRECISION = 1;

export const DEFAULT_BLUR: BlurLevel = 10;
export const DEFAULT_BLUR_VEHICLE: BlurLevel = 1;
export const BLUR_LEVEL_COEFFS = [
  0, 0.02, 0.03, 0.04, 0.05, 0.06, 0.0833, 0.1, 0.133, 0.166, 0.2, 0.266,
] as const; // use blur engine coeffs / 3 to convert to gaussian stand deviation

export const BACKGROUND_BLUR_LEVEL_COEFFS = [
  0, 0.00166, 0.0025, 0.00333, 0.005, 0.0075, 0.01, 0.0133, 0.0183, 0.0266,
  0.04,
] as const; // use inverse blur engine coeffs / 3 to convert to gaussian stand deviation

export const DEFAULT_BACKGROUND_BLUR_LEVEL = 5;

export const DEFAULT_REDACTION_CONFIGURATION: RedactionConfig = {
  fillType: 'blur',
  blurLevel: DEFAULT_BLUR,
};

export const DEFAULT_REDACTION_CONFIGURATION_VEHICLE: RedactionConfig = {
  fillType: 'blur',
  blurLevel: DEFAULT_BLUR_VEHICLE,
};

export const REDACTION_CODE_COLORS = [
  '#FFFFFF',
  '#FDD835',
  '#FF6F00',
  '#B71C1C',
  '#1B1D1F',
];

export const TEXT_OVERLAY_COLORS = [
  '#FFFFFF',
  '#1B1D1F',
  '#FDD835',
  '#FF8A80',
  '#71C562',
  '#87D7FF',
] as const;

// Preset background shading colours (first is the default dark shade):
// default, black, white, red, yellow, blue, green.
export const TEXT_OVERLAY_BACKGROUND_COLORS = [
  '#111827',
  '#000000',
  '#FFFFFF',
  '#D32F2F',
  '#FDD835',
  '#1976D2',
  '#43A047',
] as const;
// Default background shading: the dark shade at ~72% opacity used previously.
export const TEXT_OVERLAY_DEFAULT_BACKGROUND_COLOR = '#111827';
export const TEXT_OVERLAY_DEFAULT_BACKGROUND_OPACITY = 0.72;

export const ALL_REDACTION_CODE_LOCATIONS = [
  'center',
  'top',
  'bottom',
  'above',
  'below',
  'left',
  'right',
] as const;
export type REDACTION_CODE_LOCATIONS = ElementOf<
  typeof ALL_REDACTION_CODE_LOCATIONS
>;

export const HEX_REGEX = /^#([0-9a-f]{3}){1,2}$/i;

export const DEFAULT_FILTER_PARAMETERS = {
  show: {
    head: true,
    laptop: true,
    licensePlate: true,
    // manual: false,
    udr: true,
    textOverlay: true,
    person: true,
    notepad: true,
    card: true,
    poim: true,
  },
  remove: {
    head: false,
    laptop: false,
    licensePlate: false,
    // manual: false,
    udr: false,
    textOverlay: false,
    person: false,
    notepad: false,
    card: false,
    poim: false,
  },
};

export const DEFAULT_OBJECT_TYPE_EFFECTS: GlobalSettings['objectTypeEffects'] =
  {
    head: {
      shapeType: 'rectangle',
      redactionConfig: DEFAULT_REDACTION_CONFIGURATION,
      scaling: 20,
      // featherType: FeatherType.MEDIUM,
    },
    udr: {
      shapeType: 'rectangle',
      redactionConfig: DEFAULT_REDACTION_CONFIGURATION,
      // featherType: FeatherType.MEDIUM,
    },
    laptop: {
      shapeType: 'rectangle',
      redactionConfig: DEFAULT_REDACTION_CONFIGURATION,
      scaling: 0,
      // featherType: FeatherType.MEDIUM,
    },
    vehicle: {
      shapeType: 'rectangle',
      redactionConfig: DEFAULT_REDACTION_CONFIGURATION_VEHICLE,
      scaling: 0,
      // featherType: FeatherType.MEDIUM,
    },
    plate: {
      shapeType: 'rectangle',
      redactionConfig: DEFAULT_REDACTION_CONFIGURATION,
      scaling: 20,
      // featherType: FeatherType.MEDIUM,
    },
    notepad: {
      shapeType: 'rectangle',
      redactionConfig: DEFAULT_REDACTION_CONFIGURATION,
      scaling: 0,
      // featherType: FeatherType.MEDIUM,
    },
    card: {
      shapeType: 'rectangle',
      redactionConfig: DEFAULT_REDACTION_CONFIGURATION,
      scaling: 0,
      // featherType: FeatherType.MEDIUM,
    },
    person: {
      shapeType: 'rectangle',
      redactionConfig: DEFAULT_REDACTION_CONFIGURATION,
      scaling: 0,
      // featherType: FeatherType.MEDIUM,
    },
  };

export const DEFAULT_GLOBAL_SETTINGS: GlobalSettings = {
  faceDetectionThreshold: 60,
  videoType: 'Bodycam',
  detectionRate: 3,
  autoInterpolationMax: 1000,
  manualInterpolationMax: 0,
  interpolationMaxStretch: 2.5,
  trackingBackwardLimitSec: 60,
  trackingForwardLimitSec: 300,
  patchPreviousVersion: true,
  showNonRedactedOverlays: true,
  audioType: 0,
  videoOffset: 0,
  objectTypeEffects: DEFAULT_OBJECT_TYPE_EFFECTS,
  isSaved: false,
  legacyClustering: false,
  clusterSimThreshold: ClusterSimThresholdType.MEDIUM,
  autoAddBackgroundBlur: true,
  defaultBackgroundBlurLevel: 5,
  backgroundRedactionFillType: 'blur',
  audioToneLevel: AUDIO_TONE_LEVEL.MEDIUM,
};

// The Redact application's aiWARE application id, used wherever runtime config does not
// supply one (window.config.veritoneAppId).
export const FALLBACK_VERITONE_APP_ID = '766e9916-9536-47e9-8dcb-dc225654bab3';

// Built-in data registry ids used when runtime config does not supply the key. The lookups
// and the /admin/dependencies check both read these, so the check reports exactly the
// registry the app will actually use.
// Document comments: no environment's config sets `redactDocumentCommentsRegistryId` (VE-21982).
export const FALLBACK_DOCUMENT_COMMENTS_REGISTRY_ID =
  '68a4072a-bc1e-425f-a69f-c83f9d43a626';
// Redaction statuses: used when `redactionStatusesRegistryId` is unset.
export const FALLBACK_REDACTION_STATUSES_REGISTRY_ID =
  '04de84a5-380f-4ca4-89bc-13841bfd59db';

export const REDACT_APP_DEFAULT_PROFILE_SETTINGS: SettingsProfileType = {
  // The built-in default profile is keyed by the app id (same UUID, on purpose).
  id: FALLBACK_VERITONE_APP_ID,
  data: {
    profileName: 'Veritone Default Profile',
    isDefault: true,
    createdBy: 'Veritone',
    modifiedBy: '',
    settings: DEFAULT_GLOBAL_SETTINGS,
  },
  createdDateTime: '',
  modifiedDateTime: '',
};

export const DEFAULT_PER_PAGE = 10;
export const DEFAULT_ROWS_PER_PAGE = [10, 20, 100];
export const DEFAULT_MAXIMUM_LIMIT = 10000;

export const DAG_REFERENCE_IDS = {
  owFromHead: 'owInputFolderFromHeadDetection',
  owFromTranscription: 'owInputFolderFromTranscription',
  owFromSpeakerSeparation: 'owInputFolderFromSpeakerSeparation',
};

export const REDACTION_CODE_MIN_FONT_PX = 10; // Minimum font size in px to display
export const REDACTION_CODE_FONT_SIZE = 36 / 1080; // Maximum font size is a percent of video height. match large font size from blur engine which is 36px @1080
export const REDACTION_CODE_FONT_ASPECT_RATIO = 0.666; // typical aspect ratio for font characters
export const REDACTION_CODE_FONT_SCALING_COEFF =
  100 / REDACTION_CODE_FONT_ASPECT_RATIO; // used when selecting font size in cqw units

// Character count is a generous abuse guard (the box auto-fits). Lines are
// capped at 2 by product design; breaks are always explicit (Enter / "add line").
export const TEXT_OVERLAY_MAX_CHARACTERS = 500;
export const TEXT_OVERLAY_MAX_LINES = 2;
// Prompt shown (dimmed) in a new/empty text-overlay box; the box also hugs this
// so its size matches the placeholder panel until real text is typed.
export const TEXT_OVERLAY_PLACEHOLDER = 'Add text';
// Legibility floor. A line only shrinks below its target size to fit the frame
// width, and never past this many px (see getTextOverlayLayout).
export const TEXT_OVERLAY_MIN_FONT_PX = 14;
// Font size presets, expressed as a fraction of the frame (reference) height.
// These are shortcuts for the continuous TextOverlay.fontScale value.
export const TEXT_OVERLAY_SIZE_PRESETS: Record<TextOverlaySize, number> = {
  small: 28 / 1080,
  medium: 36 / 1080,
  large: 48 / 1080,
  xl: 72 / 1080,
};
// Bounds for the continuous fontScale (fraction of frame height). The min keeps
// the legibility floor at a 1080p reference; the max stops a drag from making
// text taller than the frame.
export const TEXT_OVERLAY_MIN_FONT_SCALE = TEXT_OVERLAY_MIN_FONT_PX / 1080;
// Upper bound at a 1080p reference (three digits fit the size field).
export const TEXT_OVERLAY_MAX_FONT_SCALE = 300 / 1080;
// "Inset from edge" presets for a text overlay pinned to a screen edge
// (Position on Screen). Stored on `screenInset` as a fraction of the smaller
// frame dimension (an equal pixel gap on both axes). Undefined = None (flush).
export const TEXT_OVERLAY_INSET_PRESETS = {
  small: 0.015,
  medium: 0.03,
  large: 0.08,
  xl: 0.16,
} as const;
export const TEXT_OVERLAY_LINE_HEIGHT = 1.2;
export const TEXT_OVERLAY_PADDING_RATIO = 12 / 1080;

// "Lower third" caption position (the `lowerThird` screenPositionVertical): the
// box CENTRE sits this fraction up from the bottom of the frame (a third up =
// two-thirds down). A non-grid preset offered via an on-frame quick-place ring.
export const TEXT_OVERLAY_LOWER_THIRD_FROM_BOTTOM = 1 / 3;

export const DEFAULT_TEXT_OVERLAY: TextOverlay = {
  text: '',
  color: '#FDD835',
  fontScale: TEXT_OVERLAY_SIZE_PRESETS.large,
  horizontalAlign: 'center',
  bold: false,
  backgroundEnabled: true,
  backgroundColor: TEXT_OVERLAY_DEFAULT_BACKGROUND_COLOR,
  backgroundOpacity: TEXT_OVERLAY_DEFAULT_BACKGROUND_OPACITY,
};

export const LOCK_REFRESH = 60_000;

export const PUNCTUATION_REGEX = /^[.,:;!?]$/;

export const MAX_PROFILE_NAME_LENGTH = 100;

export const MIN_WIDTH_TIME_LINE_ROW = 190;

export const TRANSCRIPTION_ELLIPSIS_MIN_DURATION_MS = 2000; //  minimum duration to display spacer/ellipis in transcription ...

export const OVERLAY_PREVIEW_OPTIONS: Array<{
  value: OverlayPreviewOptionsType;
  label: 'Redacted' | 'Outline' | 'Blackfill' | 'None';
}> = [
  {
    value: 'none',
    label: 'None',
  },
  {
    value: 'black_fill',
    label: 'Blackfill',
  },
  {
    value: 'outline',
    label: 'Outline',
  },
  {
    value: 'redacted',
    label: 'Redacted',
  },
];

export interface AudioPreviewOption {
  value: AudioPreviewOptionsType;
  label: 'Original' | 'Redacted' | 'Voice Mask';
}

export const AUDIO_PREVIEW_OPTIONS: Array<AudioPreviewOption> = [
  {
    value: 'original',
    label: 'Original',
  },
  {
    value: 'redacted',
    label: 'Redacted',
  },
  {
    value: 'voice_mask',
    label: 'Voice Mask',
  },
];

export const DEFAULT_EMPTY_TRANSCRIPTION: TranscriptionViewable = {
  id: uuid(),
  startOffsetMs: 0,
  stopOffsetMs: 0,
  transcription: [],
};

export const MEDIA_EXTENSIONS = [
  '.wav',
  '.mp3',
  '.mp4',
  '.mov',
  '.wma',
  '.m4a',
  '.aac',
  '.avi',
  '.mkv',
  '.webm',
  '.mpeg',
  '.m4v',
  '.mpg',
];
